import { ColumnInfo, ColumnInfoList, DisplaySetting } from 'components/shared/display_setting'
import { TableCell } from 'components/shared/widget'
import { GlobalStateContext } from 'contexts/global_state_context'
import { Extraction } from 'entities/extraction'
import { ReactNode, useContext, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Form } from 'utils/hooks'
import { ExtractionViewModel } from 'view_models/extraction_view_model'
import { SampleViewModel } from 'view_models/sample_view_model'

export const extractionColumnInfoList = (form: Form<Record<string, unknown>>): ColumnInfoList => {
    const globalState = useContext(GlobalStateContext)
    return {
        tableName: 'EXT',
        values: [
            {
                name: 'ExtractionID',
                th: true,
                width: 120,
                form: form,
                orderAttr: 'extraction_code',
                searchType: { name: 'extractionId', type: 'multiple_text' },
            },
            {
                name: '転記ID',
                th: true,
                width: 70,
                align: 'center',
                form: form,
                orderAttr: 'extraction_transcription_history_id',
                searchType: { name: 'transcriptionHistoryId', type: 'number' },
            },
            { name: '製品', th: true, width: 220 },
            { name: 'DNA Count', th: true, width: 100, align: 'center', form: form, orderAttr: 'extraction_extraction_count' },
            {
                name: '作成者',
                th: true,
                width: 120,
                form: form,
                orderAttr: 'extraction_staff_id',
                searchType: {
                    name: 'extractionStaffId',
                    type: 'multiple_select',
                    selectItems: globalState.users.map((user) => ({ label: user.fullName!, value: user.id! })),
                },
            },
            { name: '作成日', th: true, width: 120, form: form, orderAttr: 'extraction_date', searchType: { name: 'ExtractionDate', type: 'date' } },
            {
                name: 'PurifLot ID',
                th: true,
                width: 120,
                form: form,
                orderAttr: 'extraction_purification_reagent_item_id',
                searchType: { name: 'purification_reagent_item_id', type: 'text' },
            },
            {
                name: 'SpikeIn ID',
                th: true,
                width: 120,
                form: form,
                orderAttr: 'extraction_rna_spike_in_reagent_item_id',
                searchType: { name: 'rna_spike_in_reagent_item_id', type: 'text' },
            },
            {
                name: 'Ethanol ID',
                th: true,
                width: 120,
                form: form,
                orderAttr: 'extraction_ethanol_reagent_item_id',
                searchType: { name: 'ethanol_reagent_item_id', type: 'text' },
            },
            {
                name: 'FreeWater ID',
                th: true,
                width: 120,
                form: form,
                orderAttr: 'extraction_rna_free_water_reagent_item_id',
                searchType: { name: 'rna_free_water_reagent_item_id', type: 'text' },
            },
            { name: 'ExpQbit', th: true, width: 120, form: form, orderAttr: 'extraction_exp_qbit', searchType: { name: 'ExpQbit', type: 'number' } },
            { name: 'Note', th: true, width: 300, form: form, orderAttr: 'extraction_note', searchType: { name: 'Note', type: 'text' } },
            { name: 'エラー', th: true, width: 300, form: form, orderAttr: 'dna_error_note', searchType: { name: 'ErrorNote', type: 'text' } },
        ],
    }
}

export type ExtractionTableDataProps = {
    extraction?: Extraction
    displaySetting: DisplaySetting
}

export const ExtractionTableData = ({ extraction, displaySetting }: ExtractionTableDataProps): JSX.Element => {
    if (!extraction) {
        return (
            <>
                {displaySetting.columnInfoList.values.map((_: ColumnInfo, index: number) => {
                    if (displaySetting.hiddenList.includes(index)) {
                        return <></>
                    } else {
                        return <TableCell key={index}></TableCell>
                    }
                })}
            </>
        )
    }

    const vm = new ExtractionViewModel(extraction)

    const createCells = useMemo(() => {
        const sampleVm = new SampleViewModel(extraction.sample || {})
        return (
            <>
                <TableCell>{extraction.code}</TableCell>
                <TableCell align="center">{extraction.transcriptionHistory?.id || Number(extraction.transcriptionHistory)}</TableCell>
                <TableCell>{sampleVm.productItemView()}</TableCell>
                <TableCell align="center">
                    {extraction.dnaCount ? <Link to={`/dna?ExtractionId=${extraction.code}`}>{extraction.dnaCount} DNA</Link> : '-'}
                </TableCell>
                <TableCell>{extraction.user?.fullName}</TableCell>
                <TableCell>{vm.dateText()}</TableCell>
                <TableCell>
                    {extraction.purificationReagentItemId ? (
                        <Link to={`reagent_items?reagent_kind=purification&id=${extraction.purificationReagentItemId}`}>
                            ID: {extraction.purificationReagentItemId}
                        </Link>
                    ) : (
                        extraction.purificationLot
                    )}
                </TableCell>
                <TableCell>
                    {extraction.rnaSpikeInReagentItemId ? (
                        <Link to={`reagent_items?reagent_kind=rna_spike_in&id=${extraction.rnaSpikeInReagentItemId}`}>
                            ID: {extraction.rnaSpikeInReagentItemId}
                        </Link>
                    ) : (
                        extraction.spikeIn
                    )}
                </TableCell>
                <TableCell>
                    {extraction.ethanolReagentItemId && (
                        <Link to={`reagent_items?reagent_kind=ethanol&id=${extraction.ethanolReagentItemId}`}>ID: {extraction.ethanolReagentItemId}</Link>
                    )}
                </TableCell>
                <TableCell>
                    {extraction.rnaFreeWaterReagentItemId && (
                        <Link to={`reagent_items?reagent_kind=free_water&id=${extraction.rnaFreeWaterReagentItemId}`}>
                            ID: {extraction.rnaFreeWaterReagentItemId}
                        </Link>
                    )}
                </TableCell>
                <TableCell unit="ng/μl">{extraction.expQbit}</TableCell>
                <TableCell>{extraction.note}</TableCell>
                <TableCell>{extraction.errorNote}</TableCell>
            </>
        )
    }, [displaySetting.hiddenList])

    return createCells.props.children.filter((_: ReactNode, index: number) => !displaySetting.hiddenList?.includes(index)).map((cell: ReactNode) => cell)
}
